import React, { useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { AuthService } from '../../../services/auth.service';
import { useToast } from '../../../context/ToastContext';
import { RealisationPerformanceService } from '../../../services/garmentsCompliance.service';
import { IRealisationPerformance } from '../../../interfaces/garmentsCompliance.interface';
import { triggerDownload } from '../../statutoryRegisters/shared';

const money = (n?: number | null) => (n === null || n === undefined ? '—' : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

/** Dhaka calendar date as YYYY-MM-DD (never toISOString, which is UTC). */
const dhakaDate = (offsetDays = 0): string => {
    const d = new Date(Date.now() + offsetDays * 86400000);
    return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Dhaka' });
};

/** Buyer-wise export realisation performance for a date range of EXP dates. */
const RealisationPerformanceReport: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [from, setFrom] = useState(`${dhakaDate().slice(0, 4)}-01-01`);
    const [to, setTo] = useState(dhakaDate());
    const [report, setReport] = useState<IRealisationPerformance | null>(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            setReport(await RealisationPerformanceService.get(institutionId, from, to));
        } catch (e) {
            setReport(null);
            toast.showApiError(e, 'Failed to load the report');
        } finally {
            setLoading(false);
        }
    };

    const download = async () => {
        try {
            triggerDownload(await RealisationPerformanceService.downloadXlsx(institutionId, from, to), `realisation-performance-${from}-${to}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        }
    };

    return (
        <Box data-help-page="commercialReports.realisation.page">
            <Typography variant="h5" sx={{ mb: 2 }}>Realisation Performance</Typography>
            <Box data-help-section="commercialReports.realisation.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                <Box sx={{ flex: '1 1 180px', maxWidth: 220 }}>
                    <TextField data-help="commercialReports.realisation.from" size="small" fullWidth type="date" label="EXP date from" InputLabelProps={{ shrink: true }} value={from} onChange={e => setFrom(e.target.value)} />
                </Box>
                <Box sx={{ flex: '1 1 180px', maxWidth: 220 }}>
                    <TextField data-help="commercialReports.realisation.to" size="small" fullWidth type="date" label="EXP date to" InputLabelProps={{ shrink: true }} value={to} onChange={e => setTo(e.target.value)} />
                </Box>
                <Button data-help="commercialReports.realisation.run" variant="contained" size="small" onClick={load} disabled={loading || !from || !to}>Run</Button>
                <Button data-help="commercialReports.realisation.download" variant="outlined" size="small" startIcon={<FileDownloadIcon />} onClick={download} disabled={!report}>Export XLSX</Button>
            </Box>
            {loading && <CircularProgress size={24} />}
            {report && !loading && (
                <>
                    {report.notes.map(n => <Alert key={n} severity="info" sx={{ mb: 1 }}>{n}</Alert>)}
                    {report.truncated && <Alert severity="warning" sx={{ mb: 1 }}>Showing {report.rows.length} of {report.totalRows} rows.</Alert>}
                    <TableContainer data-help-section="commercialReports.realisation.table" component={Paper} data-testid="realisation-table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="commercialReports.realisation.col.buyer">Buyer</TableCell><TableCell data-help="commercialReports.realisation.col.ccy">Ccy</TableCell><TableCell data-help="commercialReports.realisation.col.exps" align="right">EXPs</TableCell>
                                    <TableCell data-help="commercialReports.realisation.col.expValue" align="right">EXP value</TableCell><TableCell data-help="commercialReports.realisation.col.realised" align="right">Realised</TableCell>
                                    <TableCell data-help="commercialReports.realisation.col.avgDays" align="right">Avg days</TableCell><TableCell data-help="commercialReports.realisation.col.inWindow" align="right">% in window</TableCell>
                                    <TableCell data-help="commercialReports.realisation.col.short" align="right">Short realisation</TableCell><TableCell data-help="commercialReports.realisation.col.sourceTax" align="right">Source tax</TableCell>
                                    <TableCell data-help="commercialReports.realisation.col.loanLiq" align="right">Loan liq.</TableCell><TableCell data-help="commercialReports.realisation.col.erq" align="right">ERQ</TableCell>
                                    <TableCell data-help="commercialReports.realisation.col.otherDed" align="right">Other ded.</TableCell><TableCell data-help="commercialReports.realisation.col.overdue" align="right">Open overdue</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report.rows.length === 0 && (
                                    <TableRow><TableCell colSpan={13} align="center"><Typography color="text.secondary">No EXP forms in this range.</Typography></TableCell></TableRow>
                                )}
                                {report.rows.map(r => (
                                    <TableRow key={`${r.buyerName}-${r.currencyCode}`} hover>
                                        <TableCell>{r.buyerName}</TableCell><TableCell>{r.currencyCode}</TableCell>
                                        <TableCell align="right">{r.expCount}</TableCell>
                                        <TableCell align="right">{money(r.expValue)}</TableCell>
                                        <TableCell align="right">{money(r.realizedValue)}</TableCell>
                                        <TableCell align="right">{r.avgDaysToRealisation ?? '—'}</TableCell>
                                        <TableCell align="right">{r.pctWithinWindow === null || r.pctWithinWindow === undefined ? '—' : `${r.pctWithinWindow}%`}</TableCell>
                                        <TableCell align="right">{money(r.shortRealisation)}</TableCell>
                                        <TableCell align="right">{money(r.deductionSourceTax)}</TableCell>
                                        <TableCell align="right">{money(r.deductionLoanLiquidation)}</TableCell>
                                        <TableCell align="right">{money(r.deductionErqRetention)}</TableCell>
                                        <TableCell align="right">{money(r.deductionOther)}</TableCell>
                                        <TableCell align="right">{money(r.openOverdueValue)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
};

export default RealisationPerformanceReport;
